import type { AdminChatRoomDetailResponse } from '@inflip/contracts/admin';
import type {
  ChatMessageResponse,
  ChatRoomJoinedEvent,
  SendChatMessageRequest,
} from '@inflip/contracts/user';
import { ChatMessageType, ChatParticipantRole } from '@inflip/shared';
import { useEffect, useRef, useState } from 'react';

import { Button, ErrorBanner, inputCls } from './ui';
import { emitWithAck, getChatSocket } from '../lib/chat-client';
import { formatTime } from '../lib/time-zone';

export interface ChatSenderInfo {
  name: string;
  role?: string;
}

export const ROLE_LABEL: Record<ChatParticipantRole, string> = {
  [ChatParticipantRole.BUYER]: '구매자',
  [ChatParticipantRole.SELLER]: '판매자',
  [ChatParticipantRole.ADMIN]: '관리자',
};

/** 방 참여자 → 보낸 사람 표시(닉네임 + 역할). 채팅 관리·주문 상세가 같이 쓴다 */
export function chatSendersOf(
  participants: AdminChatRoomDetailResponse['participants'],
): Record<string, ChatSenderInfo> {
  return Object.fromEntries(
    participants.map((p) => [
      p.userId,
      { name: p.name ?? p.userId.slice(0, 8), role: ROLE_LABEL[p.role] },
    ]),
  );
}

interface Props {
  token: string;
  roomId: string;
  /** userId → 표시 이름/역할. 없으면 id 앞 8자리로 폴백. */
  senders?: Record<string, ChatSenderInfo>;
  /** [나가기] — 방에 "인플립 운영자가 퇴장했습니다." 가 남고 열람 화면으로 돌아간다. 다시 참여할 수 있다 */
  onLeave?: () => Promise<void>;
}

/**
 * 어드민 채팅 패널 — participant 로 등록된 방에서 양측과 실시간 대화.
 * DISPUTED 전이 시 자동 추가되거나, 채팅 관리에서 "대화 참여" 로 수동 합류한 뒤 사용.
 */
export function AdminChatPanel({ token, roomId, senders, onLeave }: Props) {
  const [messages, setMessages] = useState<ChatMessageResponse[]>([]);
  const [draft, setDraft] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const socket = getChatSocket(token);
    const onConnect = () => setConnected(true);
    const onDisconnect = () => setConnected(false);
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    if (!socket.connected) socket.connect();

    let cancelled = false;
    const join = async () => {
      try {
        const ack = await emitWithAck<ChatRoomJoinedEvent>(socket, 'room:join', { roomId });
        if (cancelled) return;
        if (ack.ok && ack.data) setMessages(ack.data.history);
        else setError(ack.error?.message ?? 'join failed');
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : String(err));
      }
    };
    if (socket.connected) void join();
    else socket.once('connect', () => void join());

    const onNew = (m: ChatMessageResponse) => {
      if (m.roomId === roomId)
        setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    };
    const onSystem = (m: ChatMessageResponse) => {
      if (m.roomId === roomId)
        setMessages((prev) => (prev.some((x) => x.id === m.id) ? prev : [...prev, m]));
    };
    socket.on('message:new', onNew);
    socket.on('message:system', onSystem);

    return () => {
      cancelled = true;
      socket.off('connect', onConnect);
      socket.off('disconnect', onDisconnect);
      socket.off('message:new', onNew);
      socket.off('message:system', onSystem);
      void emitWithAck(socket, 'room:leave', { roomId }).catch(() => {});
    };
  }, [token, roomId]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages.length]);

  async function handleSend(e: React.FormEvent) {
    e.preventDefault();
    const content = draft.trim();
    if (!content) return;
    setDraft('');
    const socket = getChatSocket(token);
    const payload: SendChatMessageRequest = {
      roomId,
      type: ChatMessageType.TEXT,
      content,
      clientIdempotencyKey: crypto.randomUUID(),
    };
    try {
      const ack = await emitWithAck<ChatMessageResponse>(socket, 'message:send', payload);
      if (!ack.ok) throw new Error(ack.error?.message ?? 'send failed');
      setSendError(null);
    } catch (err) {
      // 종료된 방(CHAT_ROOM_CLOSED) 등 서버 거부 사유를 그대로 노출
      setSendError(err instanceof Error ? err.message : String(err));
      setDraft(content);
    }
  }

  async function handleLeave() {
    if (!onLeave || leaving) return;
    setLeaving(true);
    try {
      // 성공하면 부모가 열람 화면으로 바꿔 이 패널이 사라진다
      await onLeave();
    } catch (err) {
      setSendError(err instanceof Error ? err.message : String(err));
      setLeaving(false);
    }
  }

  if (error) return <ErrorBanner message={error} />;

  return (
    <div className="flex h-[70vh] flex-col rounded-lg border border-border bg-card shadow-soft">
      <header className="flex items-center justify-between gap-3 border-b border-border px-4 py-2.5 text-sm font-semibold">
        <span>채팅 — {connected ? '연결됨' : '연결 중'}</span>
        {onLeave && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => void handleLeave()}
            disabled={leaving}
            className="-my-1"
          >
            {leaving ? '나가는 중…' : '나가기'}
          </Button>
        )}
      </header>
      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto p-3 text-sm">
        {messages.map((m) =>
          m.type === ChatMessageType.SYSTEM ? (
            <div key={m.id} className="text-center text-xs text-muted-foreground">
              {m.content}
            </div>
          ) : (
            <div key={m.id} className="rounded-md bg-muted p-2.5">
              <div className="text-[10px] text-muted-foreground">
                {senderLabel(m.senderId, senders)} · {formatTime(m.createdAt)}
              </div>
              <div>{m.content}</div>
            </div>
          ),
        )}
      </div>
      {sendError && (
        <p className="border-t border-border px-3 py-2 text-xs font-semibold text-rose-600">
          {sendError}
        </p>
      )}
      <form onSubmit={handleSend} className="flex gap-2 border-t border-border p-2.5">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="중재 메시지 입력…"
          className={`min-w-0 flex-1 ${inputCls}`}
        />
        <Button type="submit" disabled={!draft.trim() || !connected}>
          전송
        </Button>
      </form>
    </div>
  );
}

function senderLabel(
  senderId: string | null,
  map: Record<string, ChatSenderInfo> | undefined,
): string {
  if (!senderId) return 'SYSTEM';
  const info = map?.[senderId];
  if (!info) return senderId.slice(0, 8);
  return info.role ? `${info.name} (${info.role})` : info.name;
}
